import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { RenderedHtml } from 'components'
import { Editor } from '@tinymce/tinymce-react'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { saveLearningHubBlockProgress } from 'api-actions'
import { getCurrentCommunity } from 'utils'

function buildEbookCopyrightText() {
  const community = getCurrentCommunity()
  const communityName = community?.name ? ` ${community.name}` : ''
  // \u00AE is the ® registered sign (WinAnsi-encoded, renders with jsPDF's Helvetica).
  return `Copyright artlook\u00AE${communityName}`
}

const VIDEO_EXPORT_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360"><rect width="640" height="360" fill="#eee"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#888" font-family="system-ui,sans-serif" font-size="16">Video</text></svg>'
  )

function unwrapCommentAnnotation(el) {
  if (!el?.classList?.contains('lh-ebook-annotation-comment')) return
  const p = el.parentNode
  if (!p) return
  el.querySelectorAll('.lh-ebook-annotation-comment__bubble').forEach(
    (bubble) => {
      if (bubble.parentNode) bubble.parentNode.removeChild(bubble)
    }
  )
  while (el.firstChild) p.insertBefore(el.firstChild, el)
  p.removeChild(el)
}

function stripLearnerAnnotationElements(root) {
  root.querySelectorAll('.lh-ebook-annotation-comment').forEach((el) => {
    unwrapCommentAnnotation(el)
  })
  root
    .querySelectorAll(
      '.lh-ebook-annotation-highlight, .lh-ebook-annotation-marker'
    )
    .forEach((el) => {
      const p = el.parentNode
      if (!p) return
      while (el.firstChild) p.insertBefore(el.firstChild, el)
      p.removeChild(el)
    })
  root.querySelectorAll('svg.lh-ebook-freehand-svg').forEach((el) => {
    el.remove()
  })
}

function tryYoutubeThumbnailFromIframe(iframe) {
  const src = iframe.getAttribute('src') || ''
  const embed = src.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/)
  if (embed) return `https://img.youtube.com/vi/${embed[1]}/hqdefault.jpg`
  const short = src.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/)
  if (short) return `https://img.youtube.com/vi/${short[1]}/hqdefault.jpg`
  const v = src.match(/[?&]v=([a-zA-Z0-9_-]{11})/)
  if (v) return `https://img.youtube.com/vi/${v[1]}/hqdefault.jpg`
  return null
}

function waitForImg(img) {
  return new Promise((resolve) => {
    if (img.complete && img.naturalWidth) {
      resolve()
      return
    }
    img.onload = () => resolve()
    img.onerror = () => resolve()
  })
}

function resolveMediaUrl(raw, doc) {
  if (!raw || typeof raw !== 'string') return ''
  const s = raw.trim()
  if (!s) return ''
  try {
    return new URL(
      s,
      doc.baseURI || doc.defaultView?.location?.href || undefined
    ).href
  } catch {
    return s
  }
}

/** Cross-origin <video> must use CORS before load or canvas export throws (tainted). */
function videoUrlsLookCrossOrigin(video, primarySrc, doc, topWin) {
  const pageOrigin = (() => {
    try {
      return new URL(topWin.location.href).origin
    } catch {
      return null
    }
  })()
  if (!pageOrigin) return false

  const check = (raw) => {
    if (!raw || typeof raw !== 'string') return false
    try {
      const u = new URL(raw.trim(), doc.baseURI || topWin.location.href)
      return /^https?:$/i.test(u.protocol) && u.origin !== pageOrigin
    } catch {
      return false
    }
  }

  if (check(primarySrc)) return true
  return [...video.querySelectorAll('source')].some((srcEl) =>
    check(srcEl.getAttribute('src') || '')
  )
}

async function replaceVideoWithStillForExport(video, topWin) {
  const doc = video.ownerDocument
  const parent = video.parentNode
  if (!parent) return

  const img = doc.createElement('img')
  img.alt = video.getAttribute('aria-label') || ''
  img.style.maxWidth = '100%'
  img.style.height = 'auto'
  img.style.display = 'block'

  const poster = video.getAttribute('poster')
  if (poster) {
    img.src = poster
    await waitForImg(img)
    parent.replaceChild(img, video)
    return
  }

  await new Promise((resolve) => {
    const src =
      video.currentSrc ||
      video.getAttribute('src') ||
      video.querySelector('source')?.getAttribute('src')
    if (!src) {
      img.src = VIDEO_EXPORT_PLACEHOLDER
      parent.replaceChild(img, video)
      resolve()
      return
    }

    const v = doc.createElement('video')
    v.muted = true
    v.defaultMuted = true
    v.playsInline = true
    v.setAttribute('playsinline', '')
    v.setAttribute('webkit-playsinline', '')
    v.preload = 'auto'
    v.width = 640
    v.height = 360

    const attrCors = (video.getAttribute('crossorigin') || '').toLowerCase()
    if (attrCors === 'use-credentials') {
      v.crossOrigin = 'use-credentials'
    } else if (videoUrlsLookCrossOrigin(video, src, doc, topWin)) {
      v.crossOrigin = 'anonymous'
    } else if (video.crossOrigin) {
      v.crossOrigin = video.crossOrigin
    }

    const sources = video.querySelectorAll('source')
    let appendedSources = 0
    if (sources.length) {
      sources.forEach((s) => {
        const href = s.getAttribute('src')
        if (!href) return
        const ns = doc.createElement('source')
        ns.setAttribute('src', href)
        const type = s.getAttribute('type')
        if (type) ns.setAttribute('type', type)
        const media = s.getAttribute('media')
        if (media) ns.setAttribute('media', media)
        v.appendChild(ns)
        appendedSources += 1
      })
    }
    if (appendedSources === 0) {
      v.src = resolveMediaUrl(src, doc)
    }

    v.style.cssText =
      'position:fixed;left:-9999px;top:0;width:640px;height:360px;opacity:0.01;pointer-events:none;object-fit:contain;'
    doc.body.appendChild(v)

    let finished = false
    let seekKicked = false
    let seekHandler = null
    let seekFallbackTimer = null
    let paintScheduled = false
    const finish = (dataUrl) => {
      if (finished) return
      finished = true
      if (timer) topWin.clearTimeout(timer)
      if (seekFallbackTimer) topWin.clearTimeout(seekFallbackTimer)
      if (seekHandler) v.removeEventListener('seeked', seekHandler)
      if (v.parentNode) v.parentNode.removeChild(v)
      img.src = dataUrl
      parent.replaceChild(img, video)
      void waitForImg(img).then(() => resolve())
    }

    const timeoutMs = 15000
    let timer = topWin.setTimeout(
      () => finish(VIDEO_EXPORT_PLACEHOLDER),
      timeoutMs
    )

    const grabFrame = () => {
      if (finished) return
      try {
        const vw = v.videoWidth
        const vh = v.videoHeight
        if (!vw || !vh) {
          finish(VIDEO_EXPORT_PLACEHOLDER)
          return
        }
        const c = topWin.document.createElement('canvas')
        c.width = Math.min(vw, 1920)
        c.height = Math.min(vh, 1080)
        const ctx = c.getContext('2d')
        if (!ctx) {
          finish(VIDEO_EXPORT_PLACEHOLDER)
          return
        }
        ctx.drawImage(v, 0, 0, c.width, c.height)
        finish(c.toDataURL('image/png'))
      } catch {
        finish(VIDEO_EXPORT_PLACEHOLDER)
      }
    }

    const seekTime = () => {
      const dur = v.duration
      if (!Number.isFinite(dur) || dur <= 0) return 0.05
      return Math.max(0.01, Math.min(0.5, dur * 0.02))
    }

    const scheduleGrab = () => {
      let ran = false
      const run = () => {
        if (finished || ran) return
        ran = true
        topWin.requestAnimationFrame(() => {
          topWin.requestAnimationFrame(() => grabFrame())
        })
      }
      if (typeof v.requestVideoFrameCallback === 'function') {
        v.requestVideoFrameCallback(() => run())
        topWin.setTimeout(run, 600)
      } else {
        run()
      }
    }

    const afterSeekPaint = () => {
      if (finished || paintScheduled) return
      paintScheduled = true
      if (seekHandler) {
        v.removeEventListener('seeked', seekHandler)
        seekHandler = null
      }
      if (seekFallbackTimer) {
        topWin.clearTimeout(seekFallbackTimer)
        seekFallbackTimer = null
      }
      const runPaint = () => {
        void (async () => {
          try {
            await v.play()
            v.pause()
          } catch {
            // decoder may still paint without play()
          }
          scheduleGrab()
        })()
      }
      try {
        const p = typeof v.decode === 'function' ? v.decode() : null
        if (p && typeof p.then === 'function')
          void p.then(runPaint).catch(runPaint)
        else runPaint()
      } catch {
        runPaint()
      }
    }

    const kickSeek = () => {
      if (finished || seekKicked) return
      seekKicked = true
      let t = seekTime()
      const dur = v.duration
      if (
        Number.isFinite(dur) &&
        dur > 0 &&
        Math.abs(v.currentTime - t) < 1e-4
      ) {
        t = Math.min(Math.max(0.02, dur * 0.05), dur * 0.5)
      }
      seekHandler = () => afterSeekPaint()
      v.addEventListener('seeked', seekHandler)
      try {
        v.currentTime = t
      } catch {
        finish(VIDEO_EXPORT_PLACEHOLDER)
        return
      }
      seekFallbackTimer = topWin.setTimeout(() => {
        if (!finished && v.readyState >= 2 && v.videoWidth) {
          afterSeekPaint()
        }
      }, 2200)
    }

    v.addEventListener(
      'canplay',
      () => {
        if (finished) return
        kickSeek()
      },
      { once: true }
    )
    v.addEventListener(
      'loadeddata',
      () => {
        if (finished) return
        if (v.readyState >= 2) kickSeek()
      },
      { once: true }
    )
    v.addEventListener(
      'loadedmetadata',
      () => {
        if (finished) return
        if (v.readyState >= 1) kickSeek()
      },
      { once: true }
    )

    v.addEventListener('error', () => finish(VIDEO_EXPORT_PLACEHOLDER))
    v.load()
  })
}

async function prepareExportMediaForPdf(container, topWin) {
  const doc = container.ownerDocument || topWin.document

  const iframeJobs = [...container.querySelectorAll('iframe')].map((iframe) => {
    const thumb = tryYoutubeThumbnailFromIframe(iframe)
    if (!thumb) return Promise.resolve()
    const parent = iframe.parentNode
    if (!parent) return Promise.resolve()
    const img = doc.createElement('img')
    img.alt = iframe.getAttribute('title') || ''
    img.crossOrigin = 'anonymous'
    img.style.maxWidth = '100%'
    img.style.height = 'auto'
    img.style.display = 'block'
    img.src = thumb
    parent.replaceChild(img, iframe)
    return waitForImg(img)
  })

  await Promise.all(iframeJobs)

  await Promise.all(
    [...container.querySelectorAll('video')].map((video) =>
      replaceVideoWithStillForExport(video, topWin)
    )
  )
}

async function captureSurfaceLegacyClippedPdf(surface, topWin, options) {
  const { blockId, marginMm, usableW, usableH, copyrightText } = options
  const canvas = await html2canvas(surface, {
    backgroundColor: '#ffffff',
    useCORS: true,
    scale: 2,
    logging: false,
  })
  if (!canvas || !canvas.width || !canvas.height) return
  let imgData
  try {
    imgData = canvas.toDataURL('image/png')
  } catch {
    return
  }
  const pdf = new jsPDF('p', 'mm', 'a4')
  const ratio = canvas.width / canvas.height
  const renderWidth = usableW
  const renderHeight = renderWidth / ratio

  const pageWidthMm = usableW + 2 * marginMm
  const pageHeightMm = usableH + 2 * marginMm

  const drawClippedPage = (imgY) => {
    pdf.saveGraphicsState()
    pdf.rect(marginMm, marginMm, usableW, usableH, null)
    pdf.clip()
    pdf.discardPath()
    pdf.addImage(imgData, 'PNG', marginMm, imgY, renderWidth, renderHeight)
    pdf.restoreGraphicsState()
  }

  // Copyright line right-aligned in the bottom margin, outside the clipped content area.
  const drawCopyrightFooter = () => {
    if (!copyrightText) return
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(9)
    pdf.setTextColor(120, 120, 120)
    pdf.text(
      copyrightText,
      pageWidthMm - marginMm,
      pageHeightMm - marginMm / 2,
      {
        align: 'right',
        baseline: 'middle',
      }
    )
  }

  let imgY = marginMm
  drawClippedPage(imgY)
  drawCopyrightFooter()
  let remaining = renderHeight - usableH
  while (remaining > 0) {
    pdf.addPage()
    imgY -= usableH
    drawClippedPage(imgY)
    drawCopyrightFooter()
    remaining -= usableH
  }
  pdf.save(`ebook-${blockId}.pdf`)
}

const propTypes = {
  block: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    description: PropTypes.string,
    ebookContent: PropTypes.string,
    ebookSavedContent: PropTypes.string,
    progress: PropTypes.object,
  }).isRequired,
}

const LearningHubEbookBlockPanel = forwardRef(
  function LearningHubEbookBlockPanel({ block }, ref) {
    const editorRef = useRef(null)
    const commentPlacementClickHandlerRef = useRef(null)
    const commentPlacementActiveRef = useRef(false)
    const pendingCommentBookmarkRef = useRef(null)
    /** 'caret' = bubble at click; 'wrap' = bubble + selected HTML preserved */
    const pendingCommentModeRef = useRef(null)
    const pendingWrapHtmlRef = useRef(null)
    const commentModalOpenRef = useRef(false)
    const viewCommentOpenRef = useRef(false)
    const openViewCommentModalRef = useRef(() => {})
    const commentTextareaRef = useRef(null)
    const onCommentToolbarClickRef = useRef(() => {})
    const markerDrawActiveRef = useRef(false)
    const lhMarkerToolbarApiRef = useRef(null)
    const exitMarkerDrawModeRef = useRef(() => {})
    const ebookExportMountRef = useRef(null)
    /** DOM node for the comment currently shown in the read-only view modal (delete target). */
    const viewCommentTargetRef = useRef(null)

    const [html, setHtml] = useState('')
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState('')
    const [savedAt, setSavedAt] = useState(null)
    const [commentPlacementHint, setCommentPlacementHint] = useState(false)
    const [commentModalOpen, setCommentModalOpen] = useState(false)
    const [commentDraft, setCommentDraft] = useState('')
    const [viewCommentOpen, setViewCommentOpen] = useState(false)
    const [viewCommentText, setViewCommentText] = useState('')
    const [markerDrawActive, setMarkerDrawActive] = useState(false)
    const toolbarContainerId = `lh-ebook-toolbar-${block.id}`

    const savingRef = useRef(false)
    const autoSaveTimerRef = useRef(null)
    const scheduleAutoSaveAfterAnnotationRef = useRef(() => {})

    commentModalOpenRef.current = commentModalOpen
    viewCommentOpenRef.current = viewCommentOpen
    markerDrawActiveRef.current = markerDrawActive
    savingRef.current = saving

    const initialHtml = useMemo(() => {
      const fromSaved =
        block.ebookSavedContent ??
        block.progress?.contentHtml ??
        block.progress?.content_html
      return fromSaved || block.ebookContent || ''
    }, [block.ebookSavedContent, block.ebookContent, block.progress])

    useEffect(() => {
      setHtml(initialHtml)
      setDirty(false)
      setError('')
      const ed = editorRef.current
      if (ed) {
        ed.setContent(initialHtml || '', { no_events: true })
      }
    }, [block.id, initialHtml])

    useEffect(() => {
      setMarkerDrawActive(false)
      markerDrawActiveRef.current = false
      lhMarkerToolbarApiRef.current?.setActive(false)
      editorRef.current
        ?.getBody()
        ?.classList.remove('lh-ebook-marker-draw-mode')
    }, [block.id])

    const exitMarkerDrawMode = useCallback((editor) => {
      markerDrawActiveRef.current = false
      setMarkerDrawActive(false)
      lhMarkerToolbarApiRef.current?.setActive(false)
      editor?.getBody()?.classList.remove('lh-ebook-marker-draw-mode')
    }, [])

    exitMarkerDrawModeRef.current = exitMarkerDrawMode

    useEffect(() => {
      if (!markerDrawActive) return undefined

      let cancelled = false
      let rafId = 0
      let innerCleanup = null

      const bind = () => {
        if (cancelled) return
        const editor = editorRef.current
        const body = editor?.getBody()
        if (!editor || !body) {
          rafId = window.requestAnimationFrame(bind)
          return
        }

        body.classList.add('lh-ebook-marker-draw-mode')

        const onKeyDownBlock = (e) => {
          if (e.key === 'Escape') return
          e.preventDefault()
          e.stopPropagation()
        }
        body.addEventListener('keydown', onKeyDownBlock, true)

        const pad = 6
        const strokeW = 2.5
        const strokeColor = '#0b84bf'
        /** Pad bbox so half the stroke width is not clipped inside the SVG viewBox. */
        const outerPad = pad + strokeW / 2
        let points = null
        let draftSvg = null
        let draftPath = null

        const doc = editor.getDoc()
        const win = doc.defaultView || window

        const toLocal = (clientX, clientY) => {
          const r = body.getBoundingClientRect()
          const ox = body.clientLeft
          const oy = body.clientTop
          return {
            x: clientX - r.left - ox + body.scrollLeft,
            y: clientY - r.top - oy + body.scrollTop,
          }
        }

        const pointerInsideBody = (clientX, clientY) => {
          const r = body.getBoundingClientRect()
          return (
            clientX >= r.left &&
            clientX <= r.right &&
            clientY >= r.top &&
            clientY <= r.bottom
          )
        }

        const appendSvgStroke = (w, h, d, leftPx, topPx) => {
          const NS = 'http://www.w3.org/2000/svg'
          const svg = doc.createElementNS(NS, 'svg')
          svg.setAttribute('class', 'lh-ebook-freehand-svg')
          svg.setAttribute('width', String(w))
          svg.setAttribute('height', String(h))
          svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
          svg.setAttribute('contenteditable', 'false')
          svg.style.position = 'absolute'
          svg.style.left = `${leftPx}px`
          svg.style.top = `${topPx}px`
          svg.style.display = 'block'
          svg.style.pointerEvents = 'auto'
          svg.style.zIndex = '3'
          svg.style.overflow = 'visible'
          const hit = doc.createElementNS(NS, 'rect')
          hit.setAttribute('x', '0')
          hit.setAttribute('y', '0')
          hit.setAttribute('width', String(w))
          hit.setAttribute('height', String(h))
          hit.setAttribute('fill', 'none')
          hit.setAttribute('pointer-events', 'all')
          hit.setAttribute('class', 'lh-ebook-freehand-svg__hit')
          const path = doc.createElementNS(NS, 'path')
          path.setAttribute('fill', 'none')
          path.setAttribute('stroke', strokeColor)
          path.setAttribute('stroke-width', String(strokeW))
          path.setAttribute('stroke-linecap', 'round')
          path.setAttribute('stroke-linejoin', 'round')
          path.setAttribute('d', d)
          path.setAttribute('pointer-events', 'stroke')
          svg.appendChild(hit)
          svg.appendChild(path)
          editor.undoManager.transact(() => {
            body.appendChild(svg)
          })
        }

        const removeDraft = () => {
          if (draftSvg && draftSvg.parentNode) {
            draftSvg.parentNode.removeChild(draftSvg)
          }
          draftSvg = null
          draftPath = null
        }

        const ensureDraft = () => {
          if (draftSvg) return
          const NS = 'http://www.w3.org/2000/svg'
          draftSvg = doc.createElementNS(NS, 'svg')
          draftSvg.setAttribute(
            'class',
            'lh-ebook-freehand-svg lh-ebook-freehand-svg--draft'
          )
          draftSvg.setAttribute('contenteditable', 'false')
          draftSvg.style.position = 'absolute'
          draftSvg.style.left = '0'
          draftSvg.style.top = '0'
          draftSvg.style.display = 'block'
          draftSvg.style.pointerEvents = 'none'
          draftSvg.style.zIndex = '4'
          draftSvg.style.overflow = 'visible'
          draftPath = doc.createElementNS(NS, 'path')
          draftPath.setAttribute('fill', 'none')
          draftPath.setAttribute('stroke', strokeColor)
          draftPath.setAttribute('stroke-width', String(strokeW))
          draftPath.setAttribute('stroke-linecap', 'round')
          draftPath.setAttribute('stroke-linejoin', 'round')
          draftSvg.appendChild(draftPath)
          editor.undoManager.ignore(() => {
            body.appendChild(draftSvg)
          })
        }

        const refreshDraft = (pts) => {
          if (!pts?.length) return
          ensureDraft()
          const bw = Math.max(1, body.scrollWidth)
          const bh = Math.max(1, body.scrollHeight)
          draftSvg.setAttribute('width', String(bw))
          draftSvg.setAttribute('height', String(bh))
          draftSvg.setAttribute('viewBox', `0 0 ${bw} ${bh}`)
          const drawPts =
            pts.length === 1
              ? [...pts, { x: pts[0].x + 0.5, y: pts[0].y + 0.5 }]
              : pts
          const d = drawPts
            .map(
              (p, i) =>
                `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(4)} ${p.y.toFixed(4)}`
            )
            .join(' ')
          editor.undoManager.ignore(() => {
            draftPath.setAttribute('d', d)
          })
        }

        const finalizeStroke = () => {
          const pts = points
          points = null
          removeDraft()
          if (!pts || !pts.length || !editor) return
          if (pts.length === 1) {
            pts.push({ x: pts[0].x + 1, y: pts[0].y + 1 })
          }

          let minX = Infinity
          let minY = Infinity
          let maxX = -Infinity
          let maxY = -Infinity
          pts.forEach((p) => {
            minX = Math.min(minX, p.x)
            minY = Math.min(minY, p.y)
            maxX = Math.max(maxX, p.x)
            maxY = Math.max(maxY, p.y)
          })

          const w = Math.max(outerPad * 2, maxX - minX + outerPad * 2)
          const h = Math.max(outerPad * 2, maxY - minY + outerPad * 2)
          const ox = minX - outerPad
          const oy = minY - outerPad

          const d = pts
            .map((p, i) => {
              const x = (p.x - ox).toFixed(4)
              const y = (p.y - oy).toFixed(4)
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`
            })
            .join(' ')

          appendSvgStroke(w, h, d, ox, oy)
          editor.undoManager.dispatchChange()
          setHtml(editor.getContent())
          setDirty(true)
          scheduleAutoSaveAfterAnnotationRef.current()
        }

        const winCap = { capture: true, passive: false }

        const onWinMove = (e) => {
          if (!markerDrawActiveRef.current || !points) return
          e.preventDefault()
          points.push(toLocal(e.clientX, e.clientY))
          refreshDraft(points)
        }

        const onWinUp = () => {
          if (!points) return
          win.removeEventListener('mousemove', onWinMove, winCap)
          win.removeEventListener('mouseup', onWinUp, winCap)
          finalizeStroke()
        }

        const onBodyMouseDownCapture = (e) => {
          if (!markerDrawActiveRef.current) return
          const ev = e
          if (ev.button !== 0) return
          const cx = ev.clientX ?? 0
          const cy = ev.clientY ?? 0
          if (!pointerInsideBody(cx, cy)) return
          const t = ev.target
          if (
            t &&
            typeof t.closest === 'function' &&
            t.closest('.lh-ebook-freehand-svg')
          )
            return
          if (
            t &&
            typeof t.closest === 'function' &&
            t.closest('.tox-editor-header')
          )
            return
          ev.preventDefault()
          ev.stopPropagation()
          points = [toLocal(cx, cy)]
          refreshDraft(points)
          win.addEventListener('mousemove', onWinMove, winCap)
          win.addEventListener('mouseup', onWinUp, winCap)
        }

        const capOpts = { capture: true, passive: false }
        body.addEventListener('mousedown', onBodyMouseDownCapture, capOpts)

        innerCleanup = () => {
          body.removeEventListener('keydown', onKeyDownBlock, true)
          body.removeEventListener('mousedown', onBodyMouseDownCapture, capOpts)
          win.removeEventListener('mousemove', onWinMove, winCap)
          win.removeEventListener('mouseup', onWinUp, winCap)
          removeDraft()
          body.classList.remove('lh-ebook-marker-draw-mode')
          points = null
        }
      }

      bind()

      return () => {
        cancelled = true
        window.cancelAnimationFrame(rafId)
        innerCleanup?.()
      }
    }, [markerDrawActive])

    useEffect(() => {
      if (!markerDrawActive) return undefined
      const onKeyDown = (e) => {
        if (e.key === 'Escape') {
          exitMarkerDrawMode(editorRef.current)
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [markerDrawActive, exitMarkerDrawMode])

    const teardownCommentPlacement = useCallback((editor) => {
      commentPlacementActiveRef.current = false
      setCommentPlacementHint(false)
      const handler = commentPlacementClickHandlerRef.current
      if (editor && handler) {
        editor.off('click', handler)
      }
      commentPlacementClickHandlerRef.current = null
      editor?.getBody()?.classList.remove('lh-ebook-comment-placement')
    }, [])

    const beginCommentPlacement = useCallback((editor) => {
      const body = editor.getBody()
      if (!body) return

      const stale = commentPlacementClickHandlerRef.current
      if (stale) editor.off('click', stale)

      commentPlacementActiveRef.current = true
      setCommentPlacementHint(true)
      body.classList.add('lh-ebook-comment-placement')

      const handler = (e) => {
        if (!body.contains(e.target)) return
        if (e.target?.closest?.('.lh-ebook-annotation-comment')) return
        editor.off('click', handler)
        commentPlacementClickHandlerRef.current = null
        commentPlacementActiveRef.current = false
        body.classList.remove('lh-ebook-comment-placement')
        setCommentPlacementHint(false)
        // Capture selection immediately while the click still owns the caret; deferring (rAF)
        // can leave an empty selection after focus moves to the modal.
        pendingCommentModeRef.current = 'caret'
        pendingCommentBookmarkRef.current = editor.selection.getBookmark(
          2,
          true
        )
        pendingWrapHtmlRef.current = null
        setCommentDraft('')
        setCommentModalOpen(true)
      }
      commentPlacementClickHandlerRef.current = handler
      editor.on('click', handler)
    }, [])

    const openViewCommentModal = useCallback(
      (text) => {
        const ed = editorRef.current
        if (ed && commentPlacementActiveRef.current) {
          teardownCommentPlacement(ed)
        }
        setViewCommentText(text)
        setViewCommentOpen(true)
      },
      [teardownCommentPlacement]
    )

    const closeViewCommentModal = useCallback(() => {
      viewCommentTargetRef.current = null
      setViewCommentOpen(false)
      setViewCommentText('')
    }, [])

    const deleteViewedComment = useCallback(() => {
      const editor = editorRef.current
      const el = viewCommentTargetRef.current
      if (!editor || !el) {
        closeViewCommentModal()
        return
      }
      const body = editor.getBody()
      if (!body?.contains(el)) {
        closeViewCommentModal()
        return
      }
      editor.focus()
      editor.undoManager.transact(() => {
        unwrapCommentAnnotation(el)
        editor
          .getBody()
          ?.querySelectorAll('.lh-ebook-annotation-comment__bubble')
          .forEach((b) => {
            if (!b.closest('.lh-ebook-annotation-comment')) b.remove()
          })
      })
      viewCommentTargetRef.current = null
      setHtml(editor.getContent())
      setDirty(true)
      scheduleAutoSaveAfterAnnotationRef.current()
      closeViewCommentModal()
    }, [closeViewCommentModal])

    openViewCommentModalRef.current = openViewCommentModal

    useEffect(() => {
      if (!commentPlacementHint) return undefined
      const onKeyDown = (e) => {
        if (e.key === 'Escape') {
          const editor = editorRef.current
          if (editor) teardownCommentPlacement(editor)
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [commentPlacementHint, teardownCommentPlacement])

    useEffect(() => {
      return () => {
        const editor = editorRef.current
        if (editor) teardownCommentPlacement(editor)
      }
    }, [block.id, teardownCommentPlacement])

    useEffect(() => {
      if (commentModalOpen) {
        commentTextareaRef.current?.focus()
      }
    }, [commentModalOpen])

    const closeCommentModal = useCallback(() => {
      pendingCommentBookmarkRef.current = null
      pendingCommentModeRef.current = null
      pendingWrapHtmlRef.current = null
      setCommentModalOpen(false)
      setCommentDraft('')
    }, [])

    const submitCommentModal = useCallback(() => {
      const editor = editorRef.current
      const bookmark = pendingCommentBookmarkRef.current
      const text = commentDraft.trim()
      if (!editor || !bookmark || !text) {
        closeCommentModal()
        return
      }

      const encodedAttr = editor.dom.encode(text)
      // ZWSP keeps the bubble span from being removed as an "empty" node by the editor schema.
      const bubble =
        '<span class="lh-ebook-annotation-comment__bubble" aria-hidden="true">\u200b</span>'
      const dataAttrs = `data-lh-comment="${encodedAttr}" title="${encodedAttr}" aria-label="${encodedAttr}"`
      const inlineChunk = `<span class="lh-ebook-annotation-comment lh-ebook-annotation-comment--inline" ${dataAttrs}>${bubble}</span>`
      const mode = pendingCommentModeRef.current

      try {
        editor.focus()
        editor.selection.moveToBookmark(bookmark)

        if (mode === 'wrap') {
          const innerHtml = pendingWrapHtmlRef.current
          if (!innerHtml) {
            throw new Error('Missing selection to wrap.')
          }
          // A span cannot wrap block nodes (e.g. multiple <p>); use a div for multi-line / block selections.
          const needsBlockWrap =
            /<(?:p|div|h[1-6]|ul|ol|blockquote|table)\b/i.test(innerHtml)
          const wrapped = needsBlockWrap
            ? `<div class="lh-ebook-annotation-comment lh-ebook-annotation-comment--wrap-block" ${dataAttrs}>${bubble}${innerHtml}</div>`
            : `<span class="lh-ebook-annotation-comment" ${dataAttrs}>${bubble}${innerHtml}</span>`
          editor.selection.setContent(wrapped, { format: 'html' })
        } else {
          // insertContent replaces any non-collapsed range — collapse to the saved caret first.
          const rng = editor.selection.getRng()
          if (rng && !rng.collapsed) {
            rng.collapse(true)
            editor.selection.setRng(rng)
          }
          editor.insertContent(inlineChunk, { format: 'html' })
        }

        // Mirror editor HTML for Save (no `value=` on Editor, so this does not re-parse the whole doc in TinyMCE).
        setHtml(editor.getContent())
        setDirty(true)
        scheduleAutoSaveAfterAnnotationRef.current()
      } catch (err) {
        setError(err?.message || 'Could not place comment. Try again.')
      }
      pendingCommentBookmarkRef.current = null
      pendingCommentModeRef.current = null
      pendingWrapHtmlRef.current = null
      setCommentModalOpen(false)
      setCommentDraft('')
    }, [commentDraft, closeCommentModal])

    useEffect(() => {
      if (!viewCommentOpen) return undefined
      const onKeyDown = (e) => {
        if (e.key === 'Escape') {
          closeViewCommentModal()
        }
      }
      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }, [viewCommentOpen, closeViewCommentModal])

    onCommentToolbarClickRef.current = () => {
      const editor = editorRef.current
      if (
        !editor?.getBody() ||
        commentModalOpenRef.current ||
        viewCommentOpenRef.current
      )
        return

      if (markerDrawActiveRef.current) {
        exitMarkerDrawModeRef.current(editor)
      }

      if (commentPlacementActiveRef.current) {
        teardownCommentPlacement(editor)
        return
      }

      const sel = editor.selection
      const selectedText = sel.getContent({ format: 'text' }).trim()
      if (!sel.isCollapsed() && selectedText) {
        pendingCommentModeRef.current = 'wrap'
        pendingWrapHtmlRef.current = sel.getContent({ format: 'html' })
        pendingCommentBookmarkRef.current = sel.getBookmark(2, true)
        setCommentDraft('')
        setCommentModalOpen(true)
        return
      }

      beginCommentPlacement(editor)
    }

    const saveHtml = useCallback(
      async (nextHtml) => {
        if (!nextHtml) return
        setSaving(true)
        setError('')
        try {
          await saveLearningHubBlockProgress(block.id, {
            type: 'EBOOK',
            mode: 'tinymce-inline',
            content_html: nextHtml,
          })
          setDirty(false)
          setSavedAt(new Date())
        } catch (err) {
          setError(err?.message || 'Unable to save e-book annotations.')
        } finally {
          setSaving(false)
        }
      },
      [block.id]
    )

    useImperativeHandle(
      ref,
      () => ({
        completeAndAdvance: async () => {
          const ed = editorRef.current
          const content_html = ed ? ed.getContent() : html
          await saveLearningHubBlockProgress(block.id, {
            type: 'EBOOK',
            mode: 'tinymce-inline',
            content_html,
            complete: true,
          })
        },
      }),
      [block.id, html]
    )

    const scheduleAutoSaveAfterAnnotation = useCallback(() => {
      if (typeof window === 'undefined') return
      if (autoSaveTimerRef.current) {
        window.clearTimeout(autoSaveTimerRef.current)
        autoSaveTimerRef.current = null
      }
      const delayMs = 450
      const run = () => {
        autoSaveTimerRef.current = null
        const ed = editorRef.current
        if (!ed?.getBody()) return
        if (savingRef.current) {
          autoSaveTimerRef.current = window.setTimeout(run, 350)
          return
        }
        const htmlStr = ed.getContent()
        if (!String(htmlStr).trim()) return
        void saveHtml(htmlStr)
      }
      autoSaveTimerRef.current = window.setTimeout(run, delayMs)
    }, [saveHtml])

    scheduleAutoSaveAfterAnnotationRef.current = scheduleAutoSaveAfterAnnotation

    useEffect(() => {
      return () => {
        if (autoSaveTimerRef.current) {
          window.clearTimeout(autoSaveTimerRef.current)
          autoSaveTimerRef.current = null
        }
      }
    }, [block.id])

    const exportCurrentHtml = useCallback(async () => {
      if (typeof window === 'undefined') return
      const topWin = window
      const mount = ebookExportMountRef.current
      if (!mount) return

      let raw = (block.ebookContent && String(block.ebookContent).trim()) || ''
      if (!raw) raw = '<p></p>'

      const surface = topWin.document.createElement('div')
      surface.className =
        'learning-hub-block-player__ebook-export-surface mce-content-body'
      surface.setAttribute('aria-hidden', 'true')
      surface.innerHTML = raw
      stripLearnerAnnotationElements(surface)

      mount.appendChild(surface)

      try {
        await prepareExportMediaForPdf(surface, topWin)
        try {
          await topWin.document.fonts?.ready
        } catch {
          // ignore
        }
        const pageWidthMm = 210
        const pageHeightMm = 297
        const marginMm = 15
        const usableW = Math.max(10, pageWidthMm - 2 * marginMm)
        const usableH = Math.max(10, pageHeightMm - 2 * marginMm)
        await captureSurfaceLegacyClippedPdf(surface, topWin, {
          blockId: block.id,
          marginMm,
          usableW,
          usableH,
          copyrightText: buildEbookCopyrightText(),
        })
      } finally {
        surface.remove()
      }
    }, [block.id, block.ebookContent])

    const setupEditor = useCallback(
      (editor) => {
        editorRef.current = editor

        // editor.formatter is not available during setup; register UI buttons only.
        // Formats are declared via the `formats` init option.
        // formatChanged is only safe to call after 'init', so we defer it there.

        editor.ui.registry.addToggleButton('lhHighlighter', {
          text: 'Highlighter',
          onAction: () =>
            editor.execCommand('mceToggleFormat', false, 'lhHighlight'),
          onSetup: (api) => {
            let unbind
            editor.on('init', () => {
              unbind = editor.formatter.formatChanged(
                'lhHighlight',
                (state) => {
                  api.setActive(state)
                }
              )
            })
            return () => unbind?.()
          },
        })

        editor.ui.registry.addToggleButton('lhMarker', {
          text: 'Marker',
          tooltip:
            'Draw freehand marks (pencil). Turn Marker off, then click a stroke to remove it. Click again or Esc to stop drawing.',
          onAction: (api) => {
            // Do not rely on api.isActive() timing vs TinyMCE's internal toggle — drive from our ref.
            const on = !markerDrawActiveRef.current
            markerDrawActiveRef.current = on
            api.setActive(on)
            setMarkerDrawActive(on)
            const b = editor.getBody()
            if (on) b?.classList.add('lh-ebook-marker-draw-mode')
            else b?.classList.remove('lh-ebook-marker-draw-mode')
          },
          onSetup: (api) => {
            lhMarkerToolbarApiRef.current = api
            return () => {
              if (lhMarkerToolbarApiRef.current === api) {
                lhMarkerToolbarApiRef.current = null
              }
            }
          },
        })

        editor.ui.registry.addButton('lhComment', {
          text: 'Comment',
          onAction: () => onCommentToolbarClickRef.current(),
        })

        editor.ui.registry.addButton('lhExport', {
          text: 'Export',
          tooltip:
            'Download the original e-book as PDF (no highlights, comments, or markers). Long content continues on extra pages; videos use a poster or captured frame when possible.',
          onAction: () => {
            exportCurrentHtml()
          },
        })

        const onViewSavedCommentClick = (e) => {
          const el = e.target?.closest?.('.lh-ebook-annotation-comment')
          if (!el || !editor.getBody()?.contains(el)) return
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          const raw =
            el.getAttribute('data-lh-comment') || el.getAttribute('title') || ''
          const ta = editor.getDoc().createElement('textarea')
          ta.innerHTML = raw
          viewCommentTargetRef.current = el
          openViewCommentModalRef.current(ta.value)
        }
        editor.on('click', onViewSavedCommentClick, true)

        const onFreehandMarkerClick = (e) => {
          if (markerDrawActiveRef.current) return
          const svg = e.target?.closest?.('svg.lh-ebook-freehand-svg')
          if (!svg || svg.classList.contains('lh-ebook-freehand-svg--draft'))
            return
          if (!editor.getBody()?.contains(svg)) return
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          editor.undoManager.transact(() => {
            if (svg.parentNode) svg.parentNode.removeChild(svg)
          })
          setHtml(editor.getContent())
          setDirty(true)
          scheduleAutoSaveAfterAnnotationRef.current()
        }
        editor.on('click', onFreehandMarkerClick, true)

        const onExecForAnnotationAutosave = (e) => {
          if (e.command === 'mceToggleFormat' && e.value === 'lhHighlight') {
            scheduleAutoSaveAfterAnnotationRef.current()
          }
        }
        editor.on('ExecCommand', onExecForAnnotationAutosave)

        return () => {
          editor.off('ExecCommand', onExecForAnnotationAutosave)
          editor.off('click', onViewSavedCommentClick, true)
          editor.off('click', onFreehandMarkerClick, true)
        }
      },
      [exportCurrentHtml]
    )

    return (
      <div className="learning-hub-block-player__panel">
        {block.description ? (
          <RenderedHtml>{block.description}</RenderedHtml>
        ) : null}
        {initialHtml ? (
          <div
            ref={ebookExportMountRef}
            className="learning-hub-block-player__ebook-content"
          >
            <div
              id={toolbarContainerId}
              className="learning-hub-block-player__ebook-toolbar-host"
            />
            {markerDrawActive ? (
              <p
                className="learning-hub-block-player__ebook-marker-draw-hint"
                role="status"
              >
                Marker (pencil): drag to draw. Turn Marker off and click a
                stroke to erase it. Click Marker again or Esc to stop drawing.
              </p>
            ) : null}
            {commentPlacementHint ? (
              <p
                className="learning-hub-block-player__ebook-comment-placement-hint"
                role="status"
              >
                Click where you want the comment bubble, or select text first
                and click Comment to keep that text. Press Esc to cancel.
              </p>
            ) : null}
            <Editor
              key={`lh-ebook-editor-${block.id}`}
              inline
              tagName="div"
              initialValue={initialHtml}
              onEditorChange={(next) => {
                setHtml(next)
                setDirty(true)
              }}
              apiKey={process.env.TINYMCE_API_KEY}
              init={{
                menubar: false,
                toolbar_persist: true,
                /* Toolbar UI mounts in #toolbarContainerId; sticky positioning is CSS on
                 * .learning-hub-block-player__ebook-toolbar-host (TinyMCE skips internal
                 * toolbar_sticky when fixed_toolbar_container is set). */
                fixed_toolbar_container: `#${toolbarContainerId}`,
                toolbar_mode: 'wrap',
                toolbar: 'lhHighlighter lhComment lhMarker lhExport',
                plugins: 'lists link',
                branding: false,
                paste_as_text: false,
                // Inline mode: getBody() is .mce-content-body, not <body>. Iframe mode still has body.
                // Heading sizes must match the course builder (Quill .ql-editor)
                // so H1/H2/H3 render identically in the player. Injecting them via
                // content_style beats TinyMCE's own inline content CSS.
                content_style:
                  'body{position:relative} .mce-content-body{position:relative}' +
                  '.mce-content-body h1{font-family:"Open Sans",sans-serif;font-size:36px;font-weight:700;margin:.5em 0 .3em}' +
                  '.mce-content-body h2{font-family:"Open Sans",sans-serif;font-size:28px;font-weight:600;margin:.5em 0 .3em}' +
                  // H3 = dark "section header" bar, matching the course builder.
                  '.mce-content-body h3{font-family:"Open Sans",sans-serif;font-size:20px;font-weight:700;color:#fff;background:#36464f;padding:8px 15px;margin:.5em 0 .3em}',
                extended_valid_elements:
                  // `style` is kept on span/div so authored text colors and
                  // similar inline formatting survive when content loads.
                  'span[class|title|aria-label|data-lh-comment|aria-hidden|role|style],div[class|title|aria-label|data-lh-comment|style],' +
                  'svg[class|width|height|viewBox|xmlns|contenteditable|preserveAspectRatio|style],' +
                  'path[d|fill|stroke|stroke-width|stroke-linecap|stroke-linejoin|class|style]',
                // Custom formats (setup runs before formatter exists; lhMarker is freehand SVG, not a format).
                formats: {
                  lhHighlight: {
                    inline: 'span',
                    classes: 'lh-ebook-annotation-highlight',
                  },
                },
                // UI buttons (lh*) must be registered in setup before toolbar is built.
                setup: setupEditor,
              }}
            />
            <div className="learning-hub-block-player__ebook-actions">
              <button
                type="button"
                className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
                disabled={saving || !dirty}
                onClick={() =>
                  saveHtml(editorRef.current?.getContent?.() ?? html)
                }
              >
                {saving ? 'Saving...' : 'Save annotations'}
              </button>
              {savedAt ? (
                <span className="learning-hub-block-player__ebook-saved-at">
                  Saved {savedAt.toLocaleTimeString()}
                </span>
              ) : null}
            </div>
            {error ? (
              <p className="learning-hub-block-player__ebook-error">{error}</p>
            ) : null}

            {viewCommentOpen ? (
              <div
                className="learning-hub-block-player__ebook-comment-modal-backdrop"
                role="presentation"
                onClick={closeViewCommentModal}
              >
                <div
                  className="learning-hub-block-player__ebook-comment-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="lh-ebook-view-comment-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 id="lh-ebook-view-comment-title">Comment</h3>
                  <label
                    className="learning-hub-block-player__ebook-comment-modal-label"
                    htmlFor="lh-ebook-view-comment-body"
                  >
                    Content
                  </label>
                  <textarea
                    id="lh-ebook-view-comment-body"
                    className="learning-hub-block-player__ebook-comment-modal-textarea learning-hub-block-player__ebook-comment-modal-textarea--readonly"
                    readOnly
                    rows={6}
                    value={viewCommentText}
                  />
                  <div className="learning-hub-block-player__ebook-comment-modal-actions">
                    <button
                      type="button"
                      className="learning-hub-form-block__btn learning-hub-form-block__btn--outline"
                      onClick={deleteViewedComment}
                    >
                      Delete comment
                    </button>
                    <button
                      type="button"
                      className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
                      onClick={closeViewCommentModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {commentModalOpen ? (
              <div
                className="learning-hub-block-player__ebook-comment-modal-backdrop"
                role="presentation"
                onClick={closeCommentModal}
              >
                <div
                  className="learning-hub-block-player__ebook-comment-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="lh-ebook-comment-modal-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 id="lh-ebook-comment-modal-title">Add comment</h3>
                  <label
                    className="learning-hub-block-player__ebook-comment-modal-label"
                    htmlFor="lh-ebook-comment-modal-text"
                  >
                    Comment
                  </label>
                  <textarea
                    id="lh-ebook-comment-modal-text"
                    ref={commentTextareaRef}
                    className="learning-hub-block-player__ebook-comment-modal-textarea"
                    rows={4}
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') closeCommentModal()
                    }}
                  />
                  <div className="learning-hub-block-player__ebook-comment-modal-actions">
                    <button
                      type="button"
                      className="learning-hub-form-block__btn learning-hub-form-block__btn--outline"
                      onClick={closeCommentModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="learning-hub-form-block__btn learning-hub-form-block__btn--primary"
                      disabled={!commentDraft.trim()}
                      onClick={submitCommentModal}
                    >
                      Add comment
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="learning-hub-block-player__placeholder-note">
            E-book content is not available yet.
          </p>
        )}
      </div>
    )
  }
)

LearningHubEbookBlockPanel.displayName = 'LearningHubEbookBlockPanel'

LearningHubEbookBlockPanel.propTypes = propTypes

export default LearningHubEbookBlockPanel
